import { ApiService } from "../../../config";

/**
 * Configuração do mini-site de um perfil (módulo Presença Digital).
 *
 * O backend guarda um JSON livre; quem interpreta é o mini-site. As duas chaves
 * que decidem o que vai ao ar são `publicado` e `indexar`, e o default das duas
 * é `false` — perfil sem registro não tem página no ar. É o estado seguro, e
 * cobre o cliente vencido que nunca contratou o mini-site.
 */
export type MinisiteConfig = {
  publicado?: boolean;
  indexar?: boolean;
  template?: string;
  variacao?: string;
  cores?: Record<string, string>;
  fontes?: Record<string, string>;
  faqs?: Array<{ q: string; a: string }>;
  areaTexto?: string;
  capa?: string;
  slug?: string;
  [key: string]: unknown;
};

export type MinisiteResponse = {
  profileId: string;
  config: MinisiteConfig;
  updatedAt?: string;
};

const unwrap = <T,>(response: unknown): T | null => {
  const body = (response as { data?: { data?: T } })?.data;
  return (body?.data ?? null) as T | null;
};

export type CapaSugerida = {
  id: number;
  thumbUrl: string;
  fullUrl: string;
  photographer: string;
  alt: string;
};

export const capaApi = {
  /** Busca capas no acervo de fotos (rota de SEO já existente em produção). */
  async buscar(termo: string, n = 9): Promise<CapaSugerida[]> {
    const r = await ApiService.get(
      `/seo/stock-images?q=${encodeURIComponent(termo)}&n=${n}`,
    );
    return unwrap<CapaSugerida[]>(r) || [];
  },

  /**
   * A capa é guardada dentro do JSON de config, então precisa ser data URL —
   * link direto para o Pexels quebraria o dia que a foto saísse do ar.
   */
  async paraDataUrl(url: string): Promise<string | null> {
    const r = await ApiService.get(
      `/seo/stock-images/data-url?url=${encodeURIComponent(url)}`,
    );
    return unwrap<{ dataUrl: string }>(r)?.dataUrl || null;
  },
};

export const minisiteApi = {
  /** Devolve null quando o perfil ainda não tem mini-site configurado. */
  async get(profileId: string): Promise<MinisiteResponse | null> {
    return unwrap<MinisiteResponse>(
      await ApiService.get(`/business-profiles/${profileId}/minisite`),
    );
  },

  /** O corpo vai cru: o backend exige um objeto JSON no topo. */
  async save(
    profileId: string,
    config: MinisiteConfig,
  ): Promise<MinisiteResponse | null> {
    return unwrap<MinisiteResponse>(
      await ApiService.put(`/business-profiles/${profileId}/minisite`, config),
    );
  },
};
